// One-shot "[Otto startup]" console report, printed when the widget mounts on a page. Otto degrades
// silently when a dependency is missing (an unreachable backend just means every query errors; an
// empty filter set means nothing grounds), so this makes the connectivity state explicit: which
// backend URL the widget is pointed at, whether that backend is reachable AND healthy (its /otto_status
// probes ITS dependencies — Postgres + the OpenAI key — and reports them back with the running Otto
// version/build, so one round-trip confirms the whole chain), whether the /schema taxonomy and the
// /config rollout gate load, and —
// once the dashboard's field bands finish loading — how many filter fields Otto can actually ground
// against. Best-effort and read-only: it must never throw into the widget, and it runs once per page
// per load (remounts on navigation are deduped) so it can't spam the console.
import { filterAiStatus, getFilterSchema, getAssistantConfig } from "./service";
import { pageMountsCapability } from "../capabilities/planner/pageManifest";

const TAG = "[Otto startup]";
const ranPages = new Set();
const readyPages = new Set();

// Silent under jest: the widget suites render FilterAssistant hundreds of times against PARTIALLY
// mocked services (no filterAiStatus), so the report there is pure noise against fake wiring. The
// module's own test re-enables via the test hook below.
let forcedEnabledForTests = false;
const enabled = () => forcedEnabledForTests || process.env.NODE_ENV !== "test";

// Lazy-require like telemetry.js: config/constants throws at import when the app's env URLs are
// unset (fine in the real app, but it would crash any unit test transitively importing this module).
const apiUrl = () => {
    try {
        // eslint-disable-next-line global-require
        return require("../../../config/constants").FILTER_AI_API_URL;
    } catch (e) {
        return "(constants unavailable)";
    }
};

// Is this actually filter-ai answering, or did something else intercept the request?
//
// This guard is the whole fix for a bug that shipped to every deployed environment: the report used to call
// `/health`, which the frontend nginx answers ITSELF with the static string "healthy" (it is the frontend
// pod's own liveness route). axios resolved happily, and because a STRING has no `.database` or
// `.openai_configured`, the report announced "database UNREACHABLE" and "OpenAI key NOT SET" while Otto was
// working perfectly — sending anyone who read it after phantom infrastructure problems.
//
// So: absence of a field is NOT evidence of a broken dependency. Only trust a body that is recognisably
// filter-ai's. Same protection when a path falls through to the SPA and returns index.html.
const isBackendBody = h => !!h && typeof h === "object" && !Array.isArray(h) && typeof h.service === "string";

const describeForeignBody = h => {
    if (typeof h === "string") {
        const s = h.trim().replace(/\s+/g, " ").slice(0, 60);
        return /^<|<!doctype/i.test(s) ? `HTML page ("${s}...")` : `plain text ("${s}")`;
    }
    return `unexpected ${Array.isArray(h) ? "array" : typeof h} body`;
};

// The dependency state, ONLY for a body we've confirmed came from filter-ai. Also used for the /health 503
// body (the backend answers "unhealthy" WITH details, which axios surfaces as an error response).
const logHealthBody = h => {
    const db = h.database || {};
    if (db.status === "ok") console.info(`${TAG} ✅ backend database connected`);
    else if (db.status === "disabled") console.error(`${TAG} ❌ backend database NOT CONFIGURED — no traces/history/knowledge base`);
    else console.error(`${TAG} ❌ backend database UNREACHABLE — ${db.error || "unknown error"}`);
    if (h.openai_configured) console.info(`${TAG} ✅ backend OpenAI key configured (interpret model: ${h.model})`);
    else console.error(`${TAG} ❌ backend OpenAI key NOT SET — interpret/resolve will fail`);
};

export async function runStartupDiagnostics({ page = "unknown", projectId, projectIds, userId } = {}) {
    if (!enabled() || ranPages.has(page)) return;
    ranPages.add(page);
    const scope =
        projectId != null
            ? `project ${projectId}`
            : projectIds && projectIds.length
            ? `projects [${projectIds.join(", ")}]`
            : "no project scope yet";
    console.info(`${TAG} Otto mounted on "${page}" (${scope}) — backend: ${apiUrl()}`);

    try {
        const h = await filterAiStatus();
        if (!isBackendBody(h)) {
            // 200, but not from filter-ai. Say so plainly and stop — guessing at dependencies from a body
            // we can't read is what produced the old false alarms.
            console.error(
                `${TAG} ❌ /otto_status did not reach the backend — got a ${describeForeignBody(h)} from ${apiUrl()}. ` +
                    `Something in front of filter-ai answered it: add "otto_status" to the frontend nginx proxy ` +
                    `allowlist, or the path falls through to the SPA. Otto's dependency state is UNKNOWN, not broken.`
            );
        } else {
            console.info(
                `${TAG} ✅ backend reachable — service=${h.service} env=${h.environment} status=${h.status} ` +
                    `otto=${h.otto_version || "?"} build=${h.build || "?"}`
            );
            logHealthBody(h);
        }
    } catch (err) {
        const body = err && err.response && err.response.data;
        if (isBackendBody(body) && body.database) {
            // Reachable but degraded: /health 503s with the full dependency body — report the parts.
            console.error(`${TAG} ❌ backend reachable but UNHEALTHY — service=${body.service} env=${body.environment}`);
            logHealthBody(body);
        } else {
            console.error(`${TAG} ❌ backend UNREACHABLE at ${apiUrl()} — ${(err && err.message) || err}`);
        }
    }

    try {
        const s = await getFilterSchema();
        console.info(
            `${TAG} ✅ /schema loaded — ${(s.common_dimensions || []).length} dimensions, ` +
                `${(s.wildcard_fields || []).length} wildcard fields, ${(s.scalar_toggles || []).length} scalar toggles`
        );
    } catch (err) {
        console.error(`${TAG} ❌ /schema failed — Otto cannot build its filter taxonomy (${(err && err.message) || err})`);
    }

    try {
        const cfg = await getAssistantConfig(userId);
        if (cfg && cfg.enabled === false) console.warn(`${TAG} ⚠️ assistant DISABLED for this user (rollout gate) — launcher hidden`);
        else console.info(`${TAG} ✅ assistant enabled for this user`);
    } catch (err) {
        // The widget fails open here (backend still enforces via 503) — mirror that severity.
        console.warn(`${TAG} ⚠️ /config check failed (widget fails open) — ${(err && err.message) || err}`);
    }
}

// Logged separately from the mount report because the field bands load asynchronously: readiness can
// arrive seconds after mount, and "mounted fine but no fields ever became ready" is exactly the
// degraded state this line exists to expose.
export function reportFiltersReady({ page = "unknown", fieldCount = 0, navTargetCount = 0 } = {}) {
    if (!enabled() || readyPages.has(page)) return;
    readyPages.add(page);
    if (fieldCount > 0) {
        console.info(
            `${TAG} ✅ filter fields ready on "${page}" — ${fieldCount} fields Otto can ground/apply, ${navTargetCount} nav targets`
        );
    } else if (pageMountsCapability(page, "filters")) {
        console.warn(`${TAG} ⚠️ filter set EMPTY on "${page}" — Otto is up but has no fields to ground against`);
    } else {
        // Table/navigation pages mount no `filters` capability — an empty field set is expected there.
        console.info(`${TAG} ✅ ready on "${page}" (no filter capability on this page), ${navTargetCount} nav targets`);
    }
}

// Test-only: clear the once-per-page dedup and (optionally) force the jest gate open.
export function __resetStartupDiagnosticsForTests(forceEnabled = false) {
    ranPages.clear();
    readyPages.clear();
    forcedEnabledForTests = forceEnabled;
}
